import React from "react";
import Modal from "./Modal";
import { Close, Loader } from "../icons";
import { useGetSingleCampusQuery } from "../../store/api/campusApi";
import profileImg from "../../assets/profile.png";

export default function CampusDetailsModal({ open, onClose, campusId }) {
  const { data, isLoading, isFetching, error } = useGetSingleCampusQuery(campusId, {
    skip: !campusId || !open,
  });

  const loading = isLoading || isFetching;

  const campus = data?.data;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={null}
      panelClassName="max-w-[600px] p-0"
      centered={false}
    >
      <div className="w-full">
        <div className="flex items-center justify-between border-b border-[#E5E5E5] px-7 py-5">
          <div>
            <p className="text-xl font-bold leading-6 text-black">
              Campus Details
            </p>
            <p className="leading-[17px] text-[#868686] text-xs mt-1">
              Detailed view of campus information and assigned staff
            </p>
          </div>
          <button onClick={onClose} aria-label="close">
            <Close />
          </button>
        </div>

        <div className="p-7">
          {loading && (
            <div className="flex flex-col items-center justify-center py-10 gap-2">
              <Loader />
              <p className="text-sm text-gray-500">Loading details...</p>
            </div>
          )}

          {error && !loading && (
            <div className="text-red-500 text-center py-10">
              Failed to load campus details.
            </div>
          )}

          {!loading && campus && (
            <div className="space-y-6 text-black">
              <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
                <div className="col-span-2">
                  <p className="text-[#878686] text-xs mb-1">Campus Name</p>
                  <p className="font-semibold text-base">{campus.name}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[#878686] text-xs mb-1">Address</p>
                  <p className="font-semibold">{campus.address}</p>
                </div>
                <div>
                  <p className="text-[#878686] text-xs mb-1">Type</p>
                  <span className="inline-block bg-[#E5FFF6] text-[#2C765C] px-2.5 py-1 rounded-full text-xs font-semibold capitalize">
                    {campus.type}
                  </span>
                </div>
                <div>
                  <p className="text-[#878686] text-xs mb-1">Campus ID</p>
                  <p className="font-semibold">{campus._id}</p>
                </div>
                
                <div className="col-span-2 border-t border-gray-200 mt-2 pt-4">
                  <p className="font-bold text-sm mb-3">Assigned Staff</p>
                  <div className="grid grid-cols-2 gap-4">
                    {/* VP Section */}
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-gray-100 border border-gray-200">
                        <img 
                          src={campus.campusVp?.profilePicture?.secure_url || profileImg} 
                          alt="VP"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.onerror = null; e.target.src = profileImg; }}
                        />
                      </div>
                      <div>
                        <p className="text-[#878686] text-xs">Vice Principal</p>
                        {campus.campusVp ? (
                          <>
                            <p className="font-semibold text-sm leading-tight capitalize">{campus.campusVp.name}</p>
                            <p className="text-xs text-gray-500">{campus.campusVp.email}</p>
                          </>
                        ) : (
                          <p className="font-semibold text-sm leading-tight text-gray-400">Not Assigned</p>
                        )}
                      </div>
                    </div>

                    {/* Store Incharge Section */}
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-gray-100 border border-gray-200">
                        <img 
                          src={campus.campusStoreIncharge?.profilePicture?.secure_url || profileImg} 
                          alt="Store Incharge"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.onerror = null; e.target.src = profileImg; }}
                        />
                      </div>
                      <div>
                        <p className="text-[#878686] text-xs">Store Incharge</p>
                        {campus.campusStoreIncharge ? (
                          <>
                            <p className="font-semibold text-sm leading-tight capitalize">{campus.campusStoreIncharge.name}</p>
                            <p className="text-xs text-gray-500">{campus.campusStoreIncharge.email}</p>
                          </>
                        ) : (
                          <p className="font-semibold text-sm leading-tight text-gray-400">Not Assigned</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-span-2 border-t border-gray-200 mt-2 pt-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gray-100 border border-gray-200">
                      <img 
                        src={campus.createdBy?.profilePicture?.secure_url || profileImg} 
                        alt="Creator"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.onerror = null; e.target.src = profileImg; }}
                      />
                    </div>
                    <div>
                      <p className="text-[#878686] text-xs">Created By</p>
                      <p className="font-semibold text-sm leading-tight capitalize">{campus.createdBy?.name || "Unknown"}</p>
                      <p className="text-xs text-gray-500">{campus.createdBy?.email}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-[#878686] text-xs mb-1">Created At</p>
                  <p className="font-semibold">
                    {new Date(campus.createdAt).toLocaleDateString("en-US", {
                      timeZone: "Asia/Karachi",
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-[#878686] text-xs mb-1">Last Updated</p>
                  <p className="font-semibold">
                    {new Date(campus.updatedAt).toLocaleDateString("en-US", {
                      timeZone: "Asia/Karachi",
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
